import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createProduct,
  fetchClubInfo,
  fetchOrders,
  fetchProducts,
  fetchNextMatch,
  updateClubHistory,
  updateOrderStatus,
  deleteOrder,
  updateProduct,
  deleteProduct,
  updateNextMatch,
  updateFanionNews,
  updateExpressNews,
  updateLastMatchInfo,
  updateCalendarContent,
  fetchUsersList,
  updateUserRole,
  updateUserDiscount,
  deleteUserAccount,
  updateCategoryImages,
  updateAlbumPhotos,
  updatePartnerLogos,
  updateCarouselImages
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import categoryVetementDefault from '../assets/images/catvetement.webp';
import categoryGadgetDefault from '../assets/images/catgadget.jpg';
import logouhk from '../assets/images/logouhk.png';
import spBafang from '../assets/images/spbafang.png';
import spBana from '../assets/images/spbana.jpg';
import spCanal from '../assets/images/spcanal.jpg';
import spCrtv from '../assets/images/spcrtv.jpeg';
import spFecafoot from '../assets/images/spfecafoot.jpg';
import spMufid from '../assets/images/spmufid.webp';
import defaultNewsCards, { sanitizeNewsCards } from '../data/newsDefaults';

const categories = [
  { value: 'vetement', label: 'Vêtements' },
  { value: 'gadget', label: 'Accessoires' }
];
const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'TAILLE UNIQUE'];

const defaultCategoryImages = {
  vetement: categoryVetementDefault,
  accessoire: categoryGadgetDefault
};

const emptyCategoryImages = {
  vetement: '',
  accessoire: ''
};

const normalizeCategoryImages = (value) => {
  if (!value || typeof value !== 'object') return { ...emptyCategoryImages };
  return {
    vetement: typeof value.vetement === 'string' ? value.vetement : '',
    accessoire: typeof value.accessoire === 'string' ? value.accessoire : ''
  };
};

const formatCategoryLabel = (value) => {
  if (value === 'vetement') return 'Vêtements';
  if (value === 'accessoire' || value === 'gadget') return 'Accessoires';
  return value;
};

const PRODUCTS_PER_PAGE = 10;

const tabs = [
  { id: 'products', label: 'Gestion des produits' },
  { id: 'orders', label: 'Gestion des commandes' },
  { id: 'users', label: 'Gestion des utilisateurs' },
  { id: 'content', label: 'Gestion des contenus' }
];

const ORDER_STATUS_LABELS = {
  pending: 'En attente',
  paid: 'Commande payée',
  shipped: 'Commande expédiée'
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
};

const PICKUP_ADDRESS = '40 Rue Championnet, 75018 Paris, France';
const formatCurrency = (value) => `${Number(value || 0).toFixed(2)} €`;

const getOrderAddress = (order) => {
  if (order?.customer?.address) {
    return order.customer.address;
  }
  const provider = (order?.deliveryProvider || '').toLowerCase();
  if (provider.includes('retrait')) {
    return PICKUP_ADDRESS;
  }
  return '—';
};
const defaultNextMatch = {
  league: 'LIGUE 1 Elite One',
  dateInfo: 'Dimanche 30 Mars 2025, 20h45',
  stadium: 'Stade Municipal de Bafang',
  homeTeam: 'Unisport du Haut-Nkam',
  awayTeam: 'Fovu Club de Baham',
  homeLogo: null,
  awayLogo: null
};
const defaultPartners = [
  { name: 'SP Bafang', logo: spBafang },
  { name: 'SP Bana', logo: spBana },
  { name: 'SP Canal+', logo: spCanal },
  { name: 'SP CRTV', logo: spCrtv },
  { name: 'SP Fecafoot', logo: spFecafoot },
  { name: 'SP Mufid', logo: spMufid }
];

const normalizeAlbumPhotos = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') {
        return { src: item, title: '' };
      }
      if (item && typeof item === 'object' && typeof item.src === 'string') {
        return { src: item.src, title: item.title || '' };
      }
      return null;
    })
    .filter(Boolean);
};

const normalizeCarouselSlides = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') {
        return { image: item, headline: '', subline: '' };
      }
      const source = typeof item.image === 'string' ? item.image : item.src;
      if (!source || typeof source !== 'string') return null;
      return {
        image: source,
        headline: typeof item.headline === 'string' ? item.headline : '',
        subline: typeof item.subline === 'string' ? item.subline : ''
      };
    })
    .filter(Boolean);
};

export default function Admin() {
  const [activeTab, setActiveTab] = useState('products');
  const [history, setHistory] = useState('');
  const [historyStatus, setHistoryStatus] = useState('');
  const [productStatus, setProductStatus] = useState('');
  const [productList, setProductList] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [ordersStatus, setOrdersStatus] = useState('');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const ordersStats = useMemo(() => {
    return orders.reduce(
      (acc, order) => {
        const amount = Number(order.total) || 0;
        if (order.status === 'pending') {
          acc.pendingAmount += amount;
          acc.pendingCount += 1;
        } else if (order.status === 'shipped') {
          acc.shippedAmount += amount;
          acc.shippedCount += 1;
        }
        return acc;
      },
      { pendingAmount: 0, shippedAmount: 0, pendingCount: 0, shippedCount: 0 }
    );
  }, [orders]);
  const [usersStatus, setUsersStatus] = useState('');
  const [categoryImageStatus, setCategoryImageStatus] = useState('');
  const [calendarStatus, setCalendarStatus] = useState('');
  const [calendarPdf, setCalendarPdf] = useState(null);
  const [matchStatus, setMatchStatus] = useState('');
  const [calendarText, setCalendarText] = useState('');
  const [newsStatus, setNewsStatus] = useState('');
  const [newsCards, setNewsCards] = useState([...defaultNewsCards]);
  const [partners, setPartners] = useState([...defaultPartners]);
  const [expressNews, setExpressNews] = useState('');
  const [expressStatus, setExpressStatus] = useState('');
  const [nextMatchInfo, setNextMatchInfo] = useState({ ...defaultNextMatch });
  const [nextMatchStatus, setNextMatchStatus] = useState('');
  const [partnersStatus, setPartnersStatus] = useState('');
  const [registeredUsers, setRegisteredUsers] = useState([]);
  const [albumStatus, setAlbumStatus] = useState('');
  const [albumDraft, setAlbumDraft] = useState([]);
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [carouselStatus, setCarouselStatus] = useState('');
  const { user: currentUser } = useAuth();
  const SUPER_ADMIN_EMAIL = 'augustinhakoua@gmail.com';
  const currentUserEmail = (currentUser?.email || '').toLowerCase();
  const canModifyAdmin = currentUserEmail === SUPER_ADMIN_EMAIL;
  const [matchInfo, setMatchInfo] = useState({
    homeTeam: 'Unisport du Haut-Nkam',
    awayTeam: 'Fovu Club de Baham',
    score: 'Unisport du Haut-Nkam 3 - 0 Fovu Club de Baham',
    competition: 'Elite One',
    dateInfo: '',
    location: '',
    homeLogo: null,
    awayLogo: null
  });

  const [productForm, setProductForm] = useState({
    id: null,
    name: '',
    description: '',
    price: '',
    stock: '',
    sizes: ['M'],
    category: 'maillot',
    images: []
  });
  const [categoryImages, setCategoryImages] = useState({ ...emptyCategoryImages });
  const displayedCategoryImages = {
    vetement: categoryImages.vetement || defaultCategoryImages.vetement,
    accessoire: categoryImages.accessoire || defaultCategoryImages.accessoire
  };

  const readFilesAsDataUrls = (fileList) =>
    Promise.all(
      Array.from(fileList).map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    );

  const loadProducts = useCallback(async () => {
    setProductLoading(true);
    setProductError('');
    try {
      const data = await fetchProducts();
      setProductList(data);
    } catch (err) {
      setProductError(err.message);
    } finally {
      setProductLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClubInfo()
      .then((data) => {
        setHistory(data.history || '');
        setNewsCards(
          data.fanionNews && data.fanionNews.length ? sanitizeNewsCards(data.fanionNews) : [...defaultNewsCards]
        );
        setExpressNews(data.expressNews || '');
        if (data.lastMatch) {
          setMatchInfo((prev) => ({ ...prev, ...data.lastMatch }));
        }
        if (typeof data.calendarText === 'string') {
          setCalendarText(data.calendarText);
        }
        if (data.calendarPdf) {
          setCalendarPdf(data.calendarPdf);
        } else {
          setCalendarPdf(null);
        }
        setCategoryImages(normalizeCategoryImages(data.categoryImages));
        if (Array.isArray(data.albumPhotos)) {
          setAlbumPhotos(normalizeAlbumPhotos(data.albumPhotos));
        } else {
          setAlbumPhotos([]);
        }
        if (Array.isArray(data.partnerLogos) && data.partnerLogos.length) {
          setPartners(data.partnerLogos);
        } else {
          setPartners([...defaultPartners]);
        }
        if (Array.isArray(data.carouselImages)) {
          setCarouselImages(normalizeCarouselSlides(data.carouselImages));
        } else {
          setCarouselImages([]);
        }
      })
      .catch(() => setHistory("Impossible de charger l'histoire actuelle"));
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    fetchNextMatch()
      .then((data) => setNextMatchInfo({ ...defaultNextMatch, ...data }))
      .catch((err) => setNextMatchStatus(err.message));
  }, []);

  const loadRegisteredUsers = useCallback(async () => {
    try {
      const users = await fetchUsersList();
      if (Array.isArray(users)) {
        setRegisteredUsers(
          users.map((user) => ({
            ...user,
            id: user._id,
            isAdmin: user.role === 'admin',
            discountPercent: Number(user.discountPercent) || 0
          }))
        );
      } else {
        setRegisteredUsers([]);
      }
      setUsersStatus('');
    } catch (err) {
      setUsersStatus(err.message);
    }
  }, []);

  useEffect(() => {
    loadRegisteredUsers();
  }, [loadRegisteredUsers]);

  const handleCategoryImagesSubmit = async (event) => {
    event.preventDefault();
    try {
      const response = await updateCategoryImages(categoryImages);
      setCategoryImages(normalizeCategoryImages(response));
      setCategoryImageStatus('Images mises à jour pour la boutique.');
    } catch (err) {
      setCategoryImageStatus(err.message);
    }
  };

  const handleResetCategoryImages = async () => {
    try {
      await updateCategoryImages(emptyCategoryImages);
      setCategoryImages({ ...emptyCategoryImages });
      setCategoryImageStatus('Images réinitialisées.');
    } catch (err) {
      setCategoryImageStatus(err.message);
    }
  };

  const handleHistorySubmit = async (event) => {
    event.preventDefault();
    try {
      await updateClubHistory(history, 'admin@unisport.com');
      setHistoryStatus('Histoire mise à jour avec succès.');
    } catch (err) {
      setHistoryStatus(err.message);
    }
  };

  const handleProductSubmit = async (event) => {
    event.preventDefault();
    try {
      const selectedSizes = productForm.sizes.length ? productForm.sizes : ['TAILLE UNIQUE'];
      const normalizedSizes = selectedSizes.map((label) => ({
        label,
        stock: Number(productForm.stock)
      }));
      const payload = {
        ...productForm,
        price: Number(productForm.price),
        stock: Number(productForm.stock),
        sizes: normalizedSizes,
        visuals: productForm.images
      };
      if (productForm.id) {
        await updateProduct(productForm.id, payload);
        setProductStatus('Produit mis à jour.');
      } else {
        await createProduct(payload);
        setProductStatus('Produit enregistré pour la boutique.');
        setProductPage(1);
      }
      setProductForm({
        id: null,
        name: '',
        description: '',
        price: '',
        stock: '',
        sizes: ['M'],
        category: 'maillot',
        images: []
      });
      loadProducts();
    } catch (err) {
      setProductStatus(err.message);
    }
  };

  const handleCalendarSubmit = async (event) => {
    event.preventDefault();
    try {
      await updateCalendarContent(calendarText, calendarPdf);
      setCalendarStatus('Calendrier mis à jour.');
    } catch (err) {
      setCalendarStatus(err.message);
    }
  };

  const loadOrders = useCallback(async () => {
    setOrdersStatus('');
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const data = await fetchOrders();
      setOrders(data);
    } catch (err) {
      setOrdersError(err.message);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  const handleOrderStatusChange = async (orderId, status) => {
    try {
      const updated = await updateOrderStatus(orderId, status);
      setOrders((prev) => prev.map((order) => (order._id === orderId ? updated : order)));
      setOrdersStatus('Statut mis à jour.');
    } catch (err) {
      setOrdersStatus(err.message);
    }
  };

  const handleOrderDelete = async (orderId) => {
    const confirmDelete = window.confirm('Supprimer définitivement cette commande ?');
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setOrders((prev) => prev.filter((order) => order._id !== orderId));
      setOrdersStatus('Commande supprimée.');
    } catch (err) {
      setOrdersStatus(err.message);
    }
  };

  useEffect(() => {
    if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab, loadOrders]);

  const handleMatchSubmit = async (event) => {
    event.preventDefault();
    try {
      await updateLastMatchInfo({ lastMatch: matchInfo });
      setMatchStatus('Score du dernier match mis à jour.');
    } catch (err) {
      setMatchStatus(err.message);
    }
  };

  const handleNextMatchSubmit = async (event) => {
    event.preventDefault();
    try {
      const updated = await updateNextMatch(nextMatchInfo);
      setNextMatchInfo(updated);
      setNextMatchStatus('Prochain match mis à jour.');
    } catch (err) {
      setNextMatchStatus(err.message);
    }
  };

  const handleNewsSubmit = async (event) => {
    event.preventDefault();
    try {
      await updateFanionNews(newsCards);
      setNewsStatus('Actualités mises à jour.');
    } catch (err) {
      setNewsStatus(err.message);
    }
  };

  const handleExpressSubmit = async (event) => {
    event.preventDefault();
    try {
      await updateExpressNews(expressNews);
      setExpressStatus('Actualité express mise à jour.');
    } catch (err) {
      setExpressStatus(err.message);
    }
  };

  const handlePartnersSave = async () => {
    try {
      const response = await updatePartnerLogos(partners);
      setPartners(response);
      setPartnersStatus('Liste des sponsors mise à jour.');
    } catch (err) {
      setPartnersStatus(err.message);
    }
  };

  const handlePartnerFile = (index, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPartners((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], logo: reader.result };
        return updated;
      });
    };
    reader.readAsDataURL(file);
    setPartnersStatus('');
  };

  const handleAddPartner = () => {
    setPartners((prev) => [...prev, { name: '', logo: '', id: Date.now() }]);
    setPartnersStatus('');
  };

  const handleRemovePartner = (index) => {
    setPartners((prev) => prev.filter((_, idx) => idx !== index));
    setPartnersStatus('');
  };

  const handleAlbumFiles = async (event) => {
    const { files } = event.target;
    if (!files || !files.length) {
      setAlbumDraft([]);
      return;
    }
    try {
      const photos = await readFilesAsDataUrls(files);
      const draftList = photos.map((src, index) => ({
        src,
        title: files[index]?.name?.replace(/\.[^/.]+$/, '') || ''
      }));
      setAlbumDraft(draftList);
      setAlbumStatus('');
    } catch {
      setAlbumDraft([]);
      setAlbumStatus('Impossible de lire les photos sélectionnées.');
    }
  };

  const handleAlbumSubmit = async (event) => {
    event.preventDefault();
    if (!albumDraft.length) {
      setAlbumStatus('Sélectionnez au moins une photo pour alimenter l’album.');
      return;
    }
    const addedCount = albumDraft.length;
    const updatedAlbum = [...albumPhotos, ...albumDraft];
    try {
      const response = await updateAlbumPhotos(updatedAlbum);
      setAlbumPhotos(response);
      setAlbumDraft([]);
      event.target.reset();
      setAlbumStatus(`${addedCount} photo(s) ajoutée(s) à l’album.`);
    } catch (err) {
      setAlbumStatus(err.message);
    }
  };

  const handleAlbumReset = async () => {
    try {
      await updateAlbumPhotos([]);
      setAlbumPhotos([]);
      setAlbumDraft([]);
      setAlbumStatus('Album supprimé.');
    } catch (err) {
      setAlbumStatus(err.message);
    }
  };

  const handleAlbumTitleChange = async (index, value) => {
    const updated = [...albumPhotos];
    updated[index] = { ...updated[index], title: value };
    setAlbumPhotos(updated);
    try {
      await updateAlbumPhotos(updated);
    } catch (err) {
      setAlbumStatus(err.message);
    }
  };

  const handleRemoveAlbumPhoto = async (index) => {
    const updated = albumPhotos.filter((_, idx) => idx !== index);
    setAlbumPhotos(updated);
    try {
      await updateAlbumPhotos(updated);
      setAlbumStatus('Photo supprimée.');
    } catch (err) {
      setAlbumStatus(err.message);
    }
  };

  const handleCarouselFiles = async (event) => {
    const { files } = event.target;
    if (!files || !files.length) return;
    try {
      const visuals = await readFilesAsDataUrls(files);
      setCarouselImages((prev) => [
        ...prev,
        ...visuals.map((image) => ({ image, headline: '', subline: '' }))
      ]);
      setCarouselStatus('');
      event.target.value = '';
    } catch {
      setCarouselStatus('Impossible de lire les images sélectionnées.');
    }
  };

  const handleCarouselSave = async (event) => {
    event.preventDefault();
    try {
      const response = await updateCarouselImages(carouselImages);
      setCarouselImages(normalizeCarouselSlides(response));
      setCarouselStatus('Images du carrousel mises à jour.');
    } catch (err) {
      setCarouselStatus(err.message);
    }
  };

  const handleCarouselRemove = (index) => {
    setCarouselImages((prev) => prev.filter((_, idx) => idx !== index));
    setCarouselStatus('');
  };

  const handleCarouselReset = async () => {
    try {
      const response = await updateCarouselImages([]);
      setCarouselImages(normalizeCarouselSlides(response));
      setCarouselStatus('Carrousel vidé.');
    } catch (err) {
      setCarouselStatus(err.message);
    }
  };

  const toggleUserAdmin = async (userId, isAdmin) => {
    if (!canModifyAdmin) {
      setUsersStatus("Seul le compte super-admin peut attribuer les droits administrateur.");
      return;
    }
    try {
      const role = isAdmin ? 'admin' : 'supporter';
      const response = await updateUserRole(userId, role);
      const updatedUser = response.user || response;
      setRegisteredUsers((prev) =>
        prev.map((user) => (user.id === userId ? { ...user, ...updatedUser, id: updatedUser._id, isAdmin } : user))
      );
      setUsersStatus('Rôle utilisateur mis à jour.');
    } catch (err) {
      setUsersStatus(err.message);
    }
  };

  const clampDiscountValue = (value) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return 0;
    if (parsed < 0) return 0;
    if (parsed > 100) return 100;
    return parsed;
  };

  const handleDiscountInputChange = (userId, value) => {
    const normalized = clampDiscountValue(value);
    setRegisteredUsers((prev) =>
      prev.map((user) => {
        const currentId = user.id || user._id;
        if (currentId !== userId) return user;
        return { ...user, discountPercent: normalized };
      })
    );
  };

  const handleDiscountBlur = async (userId, value) => {
    const normalized = clampDiscountValue(value);
    try {
      await updateUserDiscount(userId, normalized);
      setUsersStatus('Réduction mise à jour.');
    } catch (err) {
      setUsersStatus(err.message);
      loadRegisteredUsers();
    }
  };

  const handleUserDelete = async (userId, email) => {
    const confirmed = window.confirm(`Supprimer le compte ${email || ''} ? Cette action est irréversible.`);
    if (!confirmed) return;
    try {
      await deleteUserAccount(userId);
      setRegisteredUsers((prev) => prev.filter((user) => (user.id || user._id) !== userId));
      setUsersStatus('Compte utilisateur supprimé.');
    } catch (err) {
      setUsersStatus(err.message);
    }
  };

  const totalProductPages = Math.max(1, Math.ceil(productList.length / PRODUCTS_PER_PAGE));
  const paginatedProducts = productList.slice(
    (productPage - 1) * PRODUCTS_PER_PAGE,
    productPage * PRODUCTS_PER_PAGE
  );

  return (
    <section className="admin-section">
      <header className="admin-hero">
        <div>
          <p className="admin-eyebrow">Unisport du Haut-Nkam</p>
          <h1>Tableau de bord administrateur</h1>
          <p>Gérez le catalogue, suivez les commandes et contrôlez les accès utilisateurs.</p>
        </div>
        <nav className="admin-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={tab.id === activeTab ? 'is-active' : ''}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      {activeTab === 'products' && (
        <>
          <div className="admin-panels admin-panels--products">
            <div className="admin-card admin-card--intro">
              <h2>Gestion des produits</h2>
              <p>Enrichissez la boutique MPA’CHI avec des références inédites et gardez le contrôle sur les stocks.</p>
              <ul>
              <li>Ajoutez vos articles avec visuels et catégories.</li>
              <li>Illustrez les collections (vêtements / accessoires).</li>
              <li>Mettez à jour les prix et la disponibilité.</li>
            </ul>
          </div>

          <form className="admin-card" onSubmit={handleProductSubmit}>
            <h2>Ajouter un produit</h2>
            <label>
              Nom
              <input
                type="text"
                value={productForm.name}
                onChange={(event) => setProductForm((prev) => ({ ...prev, name: event.target.value }))}
                required
              />
            </label>
            <label>
              Description
              <textarea
                rows={4}
                value={productForm.description}
                onChange={(event) => setProductForm((prev) => ({ ...prev, description: event.target.value }))}
              />
            </label>
            <div className="admin-two-columns">
              <label>
                Prix (€)
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={productForm.price}
                  onChange={(event) => setProductForm((prev) => ({ ...prev, price: event.target.value }))}
                  required
                />
              </label>
              <label>
                Quantité en stock
                <input
                  type="number"
                  min="0"
                  value={productForm.stock}
                  onChange={(event) => setProductForm((prev) => ({ ...prev, stock: event.target.value }))}
                  required
                />
              </label>
              <label>
                Catégorie
                <select
                  value={productForm.category}
                  onChange={(event) => setProductForm((prev) => ({ ...prev, category: event.target.value }))}
                >
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Tailles disponibles
                <select
                  multiple
                  value={productForm.sizes}
                  onChange={(event) => {
                    const selected = Array.from(event.target.selectedOptions).map((option) => option.value);
                    setProductForm((prev) => ({ ...prev, sizes: selected }));
                  }}
                >
                  {sizeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <small>Sélectionnez une ou plusieurs tailles (maintenez Ctrl ou Cmd).</small>
              </label>
            </div>
            <label className="admin-file-input">
              Images du produit
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={async (event) => {
                  const { files } = event.target;
                  if (!files || !files.length) {
                    setProductForm((prev) => ({ ...prev, images: [] }));
                    return;
                  }
                  try {
                    const visuals = await readFilesAsDataUrls(files);
                    setProductForm((prev) => ({ ...prev, images: visuals }));
                  } catch {
                    setProductStatus('Impossible de lire les images sélectionnées.');
                  }
                }}
              />
              {productForm.images.length > 0 && (
                <small>{productForm.images.length} image(s) chargée(s)</small>
              )}
            </label>
            <button type="submit">Publier</button>
            {productStatus && <p className="admin-message">{productStatus}</p>}
          </form>

          <form className="admin-card" onSubmit={handleCategoryImagesSubmit}>
            <h2>Images des catégories</h2>
            <p>Affichez vos visuels dans la boutique pour Vêtements & Accessoires.</p>
            <div className="admin-two-columns">
              <label>
                Vêtements (image)
                <input
                  id="input-vetement"
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      setCategoryImages((prev) => ({ ...prev, vetement: reader.result }));
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
              <label>
                Accessoires (image)
                <input
                  id="input-accessoire"
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      setCategoryImages((prev) => ({ ...prev, accessoire: reader.result }));
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
            </div>
            <div className="admin-image-previews">
              <img src={displayedCategoryImages.vetement} alt="Catégorie vêtements" />
              <img src={displayedCategoryImages.accessoire} alt="Catégorie accessoires" />
            </div>
            <div className="admin-image-actions">
              <p className="admin-note">Sélectionnez une image puis cliquez sur sauvegarder pour conserver la catégorie.</p>
              <div className="admin-image-buttons">
                <button type="submit" className="admin-image-save">Sauvegarder</button>
                <button type="button" className="admin-image-reset" onClick={handleResetCategoryImages}>
                  Réinitialiser
                </button>
              </div>
            </div>
            {categoryImageStatus && <p className="admin-message">{categoryImageStatus}</p>}
          </form>
          </div>
          <div className="admin-card admin-product-list">
            <h2>Produits enregistrés</h2>
            {productError && <p className="admin-message">{productError}</p>}
            {productLoading ? (
              <p>Chargement des produits...</p>
            ) : productList.length === 0 ? (
              <p>Aucun produit ajouté pour le moment.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Catégorie</th>
                    <th>Prix (€)</th>
                    <th>Stock</th>
                    <th>Tailles</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedProducts.map((product) => (
                    <tr key={product._id}>
                      <td>{product.name}</td>
                      <td>{formatCategoryLabel(product.category)}</td>
                      <td>{Number(product.price).toFixed(2)}</td>
                      <td>
                        {typeof product.stock === 'number'
                          ? product.stock
                          : Array.isArray(product.sizes)
                            ? product.sizes.reduce((acc, size) => acc + (Number(size.stock) || 0), 0)
                            : 0}
                      </td>
                      <td>
                        {Array.isArray(product.sizes)
                          ? product.sizes.map((size) => (typeof size === 'string' ? size : size.label)).join(', ')
                          : product.size || '—'}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => {
                            const sizeValues = Array.isArray(product.sizes)
                              ? product.sizes.map((size) => (typeof size === 'string' ? size : size.label))
                              : product.size
                                ? [product.size]
                                : ['M'];
                            const derivedStock =
                              typeof product.stock === 'number'
                                ? product.stock
                                : Array.isArray(product.sizes) && product.sizes.length
                                  ? product.sizes[0].stock || 0
                                  : '';
                            setProductForm({
                              id: product._id,
                              name: product.name,
                              description: product.description || '',
                              price: product.price,
                              stock: derivedStock,
                              sizes: sizeValues,
                              category: product.category,
                              images: product.visuals || product.images || []
                            });
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await deleteProduct(product._id);
                              loadProducts();
                              setProductStatus('Produit supprimé.');
                            } catch (err) {
                              setProductStatus(err.message);
                            }
                          }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {productList.length > PRODUCTS_PER_PAGE && (
              <div className="admin-pagination">
                <button
                  type="button"
                  disabled={productPage === 1}
                  onClick={() => setProductPage((prev) => Math.max(1, prev - 1))}
                >
                  Précédent
                </button>
                {Array.from({ length: totalProductPages }).map((_, idx) => (
                  <button
                    key={`page-${idx + 1}`}
                    type="button"
                    className={productPage === idx + 1 ? 'is-active' : ''}
                    onClick={() => setProductPage(idx + 1)}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={productPage === totalProductPages}
                  onClick={() => setProductPage((prev) => Math.min(totalProductPages, prev + 1))}
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'orders' && (
        <div className="admin-panels">
          <div className="admin-card">
            <div className="admin-card__head">
              <div>
                <h3>Commandes</h3>
                <p>Suivez vos ventes et mettez à jour le statut (en attente / expédiée).</p>
              </div>
              <button type="button" onClick={loadOrders} disabled={ordersLoading}>
                {ordersLoading ? 'Actualisation...' : 'Actualiser'}
              </button>
            </div>
            {ordersStatus && <p className="admin-message">{ordersStatus}</p>}
            {ordersError && <p className="admin-message">{ordersError}</p>}
            {orders.length > 0 && (
              <div className="admin-order-stats">
                <article>
                  <p>Commandes en attente</p>
                  <strong>{formatCurrency(ordersStats.pendingAmount)}</strong>
                  <small>{ordersStats.pendingCount} commande(s)</small>
                </article>
                <article>
                  <p>Commandes expédiées</p>
                  <strong>{formatCurrency(ordersStats.shippedAmount)}</strong>
                  <small>{ordersStats.shippedCount} commande(s)</small>
                </article>
              </div>
            )}
            {ordersLoading ? (
              <p>Chargement des commandes...</p>
            ) : orders.length ? (
              <div className="admin-users-table-wrapper">
                <table className="admin-users-table admin-orders-table">
                  <thead>
                    <tr>
                      <th>Commande</th>
                      <th>Client</th>
                      <th>Adresse</th>
                      <th>Articles</th>
                      <th>Total (€)</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order._id}>
                        <td>
                          <strong>#{String(order._id).slice(-6)}</strong>
                          <br />
                          <small>{formatDate(order.createdAt)}</small>
                        </td>
                        <td>
                          <strong>{order.customer?.name || 'Supporter UHK'}</strong>
                          <br />
                          <span>{order.customer?.email || '—'}</span>
                          {order.customer?.phone && (
                            <>
                              <br />
                              <span>{order.customer.phone}</span>
                            </>
                          )}
                        </td>
                        <td>{getOrderAddress(order)}</td>
                        <td>
                          <ul className="admin-order-items">
                            {order.items?.map((item, idx) => (
                              <li key={`${order._id}-${idx}`}>
                                {item.name} ({item.size || 'unique'}) × {item.quantity}
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td>{Number(order.total).toFixed(2)}</td>
                        <td>
                          <select
                            value={order.status}
                            onChange={(event) => handleOrderStatusChange(order._id, event.target.value)}
                          >
                            {Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button type="button" className="admin-table-action admin-table-action--danger" onClick={() => handleOrderDelete(order._id)}>
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>Aucune commande enregistrée pour le moment.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="admin-panels">
          <div className="admin-card">
            <h3>Liste des utilisateurs</h3>
            <div className="admin-order-stats">
              <article>
                <p>Utilisateurs inscrits</p>
                <strong>{registeredUsers.length}</strong>
                <small>Total des comptes créés</small>
              </article>
              <article>
                <p>Administrateurs</p>
                <strong>{registeredUsers.filter((user) => user.isAdmin).length}</strong>
                <small>Comptes avec droits admin</small>
              </article>
            </div>
            {registeredUsers.length ? (
              <div className="admin-users-table-wrapper">
                <table className="admin-users-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Téléphone</th>
                      <th>Ville</th>
                      <th>Inscription</th>
                      <th>Rôle</th>
                      <th>% réduction</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registeredUsers.map((user) => {
                      const userId = user.id || user._id;
                      return (
                        <tr key={userId}>
                          <td>{`${user.civility || ''} ${user.firstName} ${user.lastName}`.trim()}</td>
                          <td>{user.email}</td>
                          <td>{user.phone || '—'}</td>
                          <td>{user.city || user.country || '—'}</td>
                          <td>{formatDate(user.createdAt)}</td>
                          <td>
                            <label className="admin-users-role">
                              <input
                                type="checkbox"
                                checked={!!user.isAdmin}
                                disabled={String(user.email).toLowerCase() === SUPER_ADMIN_EMAIL || !canModifyAdmin}
                                onChange={(event) => toggleUserAdmin(userId, event.target.checked)}
                                title={
                                  String(user.email).toLowerCase() === SUPER_ADMIN_EMAIL
                                    ? 'Compte super-admin protégé'
                                    : !canModifyAdmin
                                    ? 'Seul le compte super-admin peut attribuer ce droit'
                                    : ''
                                }
                              />
                              Administrateur
                            </label>
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={Number(user.discountPercent ?? 0)}
                              onChange={(event) => handleDiscountInputChange(userId, event.target.value)}
                              onBlur={(event) => handleDiscountBlur(userId, event.target.value)}
                              className="admin-users-discount-input"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="admin-table-action admin-table-action--danger"
                              onClick={() => handleUserDelete(userId, user.email)}
                              disabled={String(user.email).toLowerCase() === SUPER_ADMIN_EMAIL}
                              title={String(user.email).toLowerCase() === SUPER_ADMIN_EMAIL ? 'Impossible de supprimer le compte super-admin' : ''}
                            >
                              Supprimer
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>{usersStatus || 'Aucun utilisateur inscrit pour le moment.'}</p>
            )}
            <button type="button" onClick={loadRegisteredUsers}>
              Rafraîchir
            </button>
            {usersStatus && <p className="admin-message">{usersStatus}</p>}
          </div>
        </div>
      )}

      {activeTab === 'content' && (
        <div className="admin-panels">
          <form className="admin-card" onSubmit={handleHistorySubmit}>
            <h2>Histoire du club</h2>
            <textarea value={history} onChange={(event) => setHistory(event.target.value)} rows={10} />
            <button type="submit">Mettre à jour</button>
            {historyStatus && <p className="admin-message">{historyStatus}</p>}
          </form>

          <form className="admin-card" onSubmit={handleMatchSubmit}>
            <h2>Score du dernier match</h2>
            <div className="admin-two-columns">
              <label>
                Équipe locale
                <input
                  type="text"
                  value={matchInfo.homeTeam}
                  onChange={(event) => setMatchInfo((prev) => ({ ...prev, homeTeam: event.target.value }))}
                />
              </label>
              <label>
                Logo équipe locale
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      setMatchInfo((prev) => ({ ...prev, homeLogo: reader.result }));
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
            </div>
            <div className="admin-two-columns">
              <label>
                Équipe visiteuse
                <input
                  type="text"
                  value={matchInfo.awayTeam}
                  onChange={(event) => setMatchInfo((prev) => ({ ...prev, awayTeam: event.target.value }))}
                />
              </label>
              <label>
                Logo équipe visiteuse
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      setMatchInfo((prev) => ({ ...prev, awayLogo: reader.result }));
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
            </div>
            <div className="admin-two-columns">
              <label>
                Score
                <input
                  type="text"
                  value={matchInfo.score}
                  onChange={(event) => setMatchInfo((prev) => ({ ...prev, score: event.target.value }))}
                />
              </label>
              <label>
                Compétition
                <input
                  type="text"
                  value={matchInfo.competition}
                  onChange={(event) => setMatchInfo((prev) => ({ ...prev, competition: event.target.value }))}
                />
              </label>
            </div>
            <div className="admin-two-columns">
              <label>
                Date / heure
                <input
                  type="text"
                  value={matchInfo.dateInfo || ''}
                  onChange={(event) => setMatchInfo((prev) => ({ ...prev, dateInfo: event.target.value }))}
                />
              </label>
              <label>
                Stade
                <input
                  type="text"
                  value={matchInfo.location || ''}
                  onChange={(event) => setMatchInfo((prev) => ({ ...prev, location: event.target.value }))}
                />
              </label>
            </div>
            <button type="submit">Sauvegarder</button>
            {matchStatus && <p className="admin-message">{matchStatus}</p>}
          </form>

          <form className="admin-card" onSubmit={handleCalendarSubmit}>
            <h2>Calendrier de la saison</h2>
            <textarea
              rows={6}
              placeholder="Exemple: 21/02 - Journée 18 vs Coton Sport (Stade de Bafang)"
              value={calendarText}
              onChange={(event) => setCalendarText(event.target.value)}
            />
            <label className="admin-file-input">
              Ajouter un PDF (optionnel)
              <input
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setCalendarPdf(reader.result);
                  reader.readAsDataURL(file);
                }}
              />
            </label>
            <button type="submit">Mettre à jour</button>
            {calendarStatus && <p className="admin-message">{calendarStatus}</p>}
          </form>

          <form className="admin-card" onSubmit={handleAlbumSubmit}>
            <h2>Album photo</h2>
            <p>Publiez les photos marquantes (matchs, coulisses, supporters) pour la rubrique « Notre histoire ».</p>
            <label className="admin-file-input">
              Ajouter des photos
              <input type="file" accept="image/*" multiple onChange={handleAlbumFiles} />
            </label>
            {albumDraft.length > 0 && (
              <>
                <p className="admin-hint">{albumDraft.length} nouvelle(s) photo(s) prête(s) à être ajoutée(s).</p>
                <div className="admin-album-draft">
                  {albumDraft.map((photo, index) => (
                    <label key={`draft-${index}`}>
                      <span className="form-label">Titre pour la photo {index + 1}</span>
                      <input
                        type="text"
                        value={photo.title}
                        onChange={(event) => {
                          const updated = [...albumDraft];
                          updated[index] = { ...updated[index], title: event.target.value };
                          setAlbumDraft(updated);
                        }}
                      />
                    </label>
                  ))}
                </div>
              </>
            )}
            <div className="admin-album-actions">
              <button type="submit">Ajouter à l’album</button>
              <button type="button" className="admin-button--ghost" onClick={handleAlbumReset}>
                Vider l’album
              </button>
            </div>
          {albumStatus && <p className="admin-message">{albumStatus}</p>}
          {albumPhotos.length > 0 && (
            <div className="admin-album-preview">
              {albumPhotos.map((photo, index) => (
                <div key={`album-photo-${index}`} className="admin-album-preview__item">
                  <button
                    type="button"
                    className="admin-album-remove"
                    onClick={() => handleRemoveAlbumPhoto(index)}
                    aria-label="Supprimer la photo"
                  >
                    ×
                  </button>
                  <img src={photo.src} alt={`Album ${index + 1}`} />
                  <input
                    type="text"
                    value={photo.title}
                    onChange={(event) => handleAlbumTitleChange(index, event.target.value)}
                    placeholder="Titre de la photo"
                  />
                </div>
              ))}
            </div>
          )}
        </form>

        <form className="admin-card" onSubmit={handleExpressSubmit}>
          <h2>Actualité express</h2>
          <p>Texte court affiché dans la tuile « Actualités express » (section Dernier match).</p>
          <textarea
            rows={4}
            value={expressNews}
            onChange={(event) => {
              setExpressNews(event.target.value);
              setExpressStatus('');
            }}
            placeholder="Exemple : Nouveau maillot disponible à la boutique MPA’CHI."
          />
          <button type="submit">Mettre à jour</button>
          {expressStatus && <p className="admin-message">{expressStatus}</p>}
        </form>

        <form className="admin-card admin-card--full">
          <h2>Logos des sponsors</h2>
          <p>Ajoutez ou supprimez les logos visibles dans la section « Partenaires & Offres » de la page d’accueil.</p>
          <div className="admin-sponsor-grid">
            {partners.map((partner, index) => (
              <div key={partner.id || partner.name || `sponsor-${index}`} className="admin-sponsor-card">
                <label>
                  Nom du sponsor
                  <input
                    type="text"
                    value={partner.name}
                    onChange={(event) =>
                      setPartners((prev) => {
                        const updated = [...prev];
                        updated[index] = { ...partner, name: event.target.value };
                        return updated;
                      })
                    }
                  />
                </label>
                <label className="admin-file-input">
                  Logo
                  <input type="file" accept="image/*" onChange={(event) => handlePartnerFile(index, event.target.files?.[0])} />
                </label>
                {partner.logo && <img src={partner.logo} alt={partner.name || `Sponsor ${index + 1}`} />}
                <button type="button" className="admin-table-action admin-table-action--danger" onClick={() => handleRemovePartner(index)}>
                  Supprimer
                </button>
              </div>
            ))}
          </div>
          <div className="admin-image-actions">
            <button type="button" className="site-header__cta site-header__cta--ghost" onClick={handleAddPartner}>
              Ajouter un sponsor
            </button>
            <button type="button" className="site-header__cta" onClick={handlePartnersSave}>
              Sauvegarder les logos
            </button>
          </div>
          {partnersStatus && <p className="admin-message">{partnersStatus}</p>}
        </form>

        <form className="admin-card admin-card--full" onSubmit={handleNewsSubmit}>
          <h2>Actualités - Équipe fanion</h2>
          <p>Alimentez les trois blocs « Équipe fanion » visibles sur la page d’accueil.</p>
          <div className="admin-news-grid">
            {newsCards.map((card, index) => (
              <div key={`news-card-${index}`} className="admin-news-block">
                <label>
                  <span className="form-label">Titre du bloc {index + 1}</span>
                  <input
                    type="text"
                    value={card.title}
                    onChange={(event) => {
                      const updated = [...newsCards];
                      updated[index] = { ...updated[index], title: event.target.value };
                      setNewsCards(updated);
                    }}
                  />
                </label>
                <label>
                  <span className="form-label">Contenu</span>
                  <textarea
                    rows={5}
                    value={card.content}
                    onChange={(event) => {
                      const updated = [...newsCards];
                      updated[index] = { ...updated[index], content: event.target.value };
                      setNewsCards(updated);
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
        <div className="admin-news-actions">
          <button type="submit">Enregistrer les actualités</button>
          {newsStatus && <p className="admin-message">{newsStatus}</p>}
        </div>
      </form>

      <form className="admin-card admin-card--full" onSubmit={handleNextMatchSubmit}>
        <h2>Bandeau « Prochain match »</h2>
        <p>Mettez à jour la bannière rouge collée au carrousel sur la page d’accueil.</p>
        <div className="admin-two-columns">
          <label>
            Ligue / compétition
            <input
              type="text"
              value={nextMatchInfo.league}
              onChange={(event) => setNextMatchInfo((prev) => ({ ...prev, league: event.target.value }))}
            />
          </label>
          <label>
            Date et heure
            <input
              type="text"
              value={nextMatchInfo.dateInfo}
              onChange={(event) => setNextMatchInfo((prev) => ({ ...prev, dateInfo: event.target.value }))}
            />
          </label>
        </div>
        <label>
          Stade
          <input
            type="text"
            value={nextMatchInfo.stadium}
            onChange={(event) => setNextMatchInfo((prev) => ({ ...prev, stadium: event.target.value }))}
          />
        </label>
        <div className="admin-two-columns">
          <label>
            Équipe locale
            <input
              type="text"
              value={nextMatchInfo.homeTeam}
              onChange={(event) => setNextMatchInfo((prev) => ({ ...prev, homeTeam: event.target.value }))}
            />
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => setNextMatchInfo((prev) => ({ ...prev, homeLogo: reader.result }));
                reader.readAsDataURL(file);
              }}
            />
          </label>
          <label>
            Équipe visiteuse
            <input
              type="text"
              value={nextMatchInfo.awayTeam}
              onChange={(event) => setNextMatchInfo((prev) => ({ ...prev, awayTeam: event.target.value }))}
            />
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => setNextMatchInfo((prev) => ({ ...prev, awayLogo: reader.result }));
                reader.readAsDataURL(file);
              }}
            />
          </label>
        </div>
        <div className="admin-image-actions">
          <button type="submit" className="site-header__cta">
            Mettre à jour
          </button>
        </div>
        {nextMatchStatus && <p className="admin-message">{nextMatchStatus}</p>}
      </form>

      <form className="admin-card admin-card--full" onSubmit={handleCarouselSave}>
        <h2>Images du carrousel</h2>
        <p>Ajoutez ou supprimez les visuels du carrousel en page d’accueil. L’ordre d’affichage suit la liste ci-dessous.</p>
        <label className="admin-file-input">
          Ajouter des images
          <input type="file" accept="image/*" multiple onChange={handleCarouselFiles} />
        </label>
        {carouselImages.length > 0 ? (
          <div className="admin-album-preview admin-carousel-preview">
            {carouselImages.map((slide, index) => (
              <div key={`carousel-slide-${index}`} className="admin-album-preview__item">
                <button
                  type="button"
                  className="admin-album-remove"
                  onClick={() => handleCarouselRemove(index)}
                  aria-label={`Supprimer la diapositive ${index + 1}`}
                >
                  ×
                </button>
                <img src={slide.image} alt={`Diapositive ${index + 1}`} />
                <p>Diapositive {index + 1}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-hint">Aucune image n’est configurée pour le carrousel.</p>
        )}
        <div className="admin-image-actions">
          <button type="submit" className="site-header__cta">
            Sauvegarder le carrousel
          </button>
          <button type="button" className="site-header__cta site-header__cta--ghost" onClick={handleCarouselReset}>
            Vider le carrousel
          </button>
        </div>
        {carouselStatus && <p className="admin-message">{carouselStatus}</p>}
      </form>

      </div>
    )}
    </section>
  );
}
